import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import Header from '../components/layout/Header';
import {
  FiArrowLeft,
  FiCalendar,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiMapPin,
  FiUser,
  FiXCircle
} from 'react-icons/fi';

interface RegisteredClass {
  id: string;
  userId: string;
  semester: string;
  courseCode: string;
  courseName: string;
  section: string;
  instructor: string;
  days: string;
  time: string;
  location: string;
  credits: number;
  status: 'registered' | 'waitlisted' | 'dropped';
  registeredDate: string;
  crn: string;
}

interface RegistrationPeriod {
  semester: string;
  status: 'open' | 'closed';
  startDate: string;
  endDate: string;
  priorityGroup: string;
  addDeadline: string;
  dropDeadline: string;
}

const MyRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [classes, setClasses] = useState<RegisteredClass[]>([]);
  const [registrationPeriod, setRegistrationPeriod] = useState<RegistrationPeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSemester, setSelectedSemester] = useState('Fall 2025');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRegistrationData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch registered classes
        const classesQuery = query(
          collection(db, 'registeredClasses'),
          where('userId', '==', currentUser.uid),
          where('semester', '==', selectedSemester)
        );

        const classesSnapshot = await getDocs(classesQuery);
        const classesData = classesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as RegisteredClass));

        // Sort by courseCode in JavaScript instead of Firestore
        const sortedClasses = classesData.sort((a, b) =>
          a.courseCode.localeCompare(b.courseCode)
        );

        setClasses(sortedClasses);

        // Fetch registration period information
        const periodQuery = query(
          collection(db, 'registrationPeriods'),
          where('semester', '==', selectedSemester)
        );

        const periodSnapshot = await getDocs(periodQuery);
        if (!periodSnapshot.empty) {
          setRegistrationPeriod(periodSnapshot.docs[0].data() as RegistrationPeriod);
        }

      } catch (err) {
        console.error('Error fetching registration data:', err);
        setError('Failed to load registration data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrationData();
  }, [currentUser, selectedSemester]);

  const getStatusBadge = (status: string) => {
    const badges = {
      registered: {
        icon: <FiCheckCircle size={16} />,
        text: 'Registered',
        color: 'bg-green-100 text-green-800'
      },
      waitlisted: {
        icon: <FiClock size={16} />,
        text: 'Waitlisted',
        color: 'bg-yellow-100 text-yellow-800'
      },
      dropped: {
        icon: <FiXCircle size={16} />,
        text: 'Dropped',
        color: 'bg-red-100 text-red-800'
      }
    };

    const badge = badges[status as keyof typeof badges];
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  const totalCredits = classes
    .filter(c => c.status === 'registered')
    .reduce((sum, c) => sum + c.credits, 0);

  const registeredCount = classes.filter(c => c.status === 'registered').length;
  const waitlistedCount = classes.filter(c => c.status === 'waitlisted').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-uc-navy">Loading registration data...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">MY REGISTRATION</h1>
          <p className="text-gray-600 mt-2">View and manage your course registration</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <FiAlertCircle className="text-red-600 mt-0.5" size={20} />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Registration Status Banner */}
        {registrationPeriod && registrationPeriod.status === 'closed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <FiAlertCircle className="text-red-600 mt-1" size={24} />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-red-900 mb-2">Registration Closed</h2>
                <p className="text-red-800 mb-4">
                  Registration for {registrationPeriod.semester} is currently closed.
                  The registration period was from {registrationPeriod.startDate} to {registrationPeriod.endDate}.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-red-900">Add/Drop Deadline:</p>
                    <p className="text-red-800">{registrationPeriod.dropDeadline}</p>
                  </div>
                  <div>
                    <p className="font-medium text-red-900">Your Priority Group:</p>
                    <p className="text-red-800">{registrationPeriod.priorityGroup}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Semester Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <FiCalendar className="text-uc-gold" size={24} />
              <h2 className="text-xl font-bold text-uc-navy">Select Semester</h2>
            </div>
            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-uc-gold"
            >
              <option value="Fall 2025">Fall 2025</option>
              <option value="Spring 2026">Spring 2026</option>
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Total Credits</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">{totalCredits}</p>
              </div>
              <FiCheckCircle className="text-uc-gold" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Registered Classes</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">{registeredCount}</p>
              </div>
              <FiCalendar className="text-uc-gold" size={40} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium uppercase">Waitlisted</p>
                <p className="text-3xl font-bold text-uc-navy mt-2">{waitlistedCount}</p>
              </div>
              <FiClock className="text-uc-gold" size={40} />
            </div>
          </div>
        </div>

        {/* Registered Classes */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-uc-navy">Your Registered Classes</h2>
            <p className="text-gray-600 text-sm mt-1">
              {selectedSemester} • {registeredCount} {registeredCount === 1 ? 'Class' : 'Classes'}
            </p>
          </div>

          {classes.length === 0 ? (
            <div className="p-12 text-center">
              <FiCalendar size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No classes registered for {selectedSemester}</p>
              <p className="text-gray-400 text-sm mt-2">
                Check back during the registration period to add classes
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Course
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Section
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instructor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schedule
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CRN
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {classes.map((classItem) => (
                    <tr key={classItem.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-uc-navy">{classItem.courseCode}</p>
                          <p className="text-sm text-gray-600">{classItem.courseName}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-900">
                        {classItem.section}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FiUser className="text-gray-400" size={16} />
                          <span className="text-gray-900">{classItem.instructor}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-900 font-medium">{classItem.days}</p>
                          <p className="text-gray-600">{classItem.time}</p>
                          <div className="flex items-center gap-1 text-gray-500 mt-1">
                            <FiMapPin size={12} />
                            <span>{classItem.location}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        {classItem.credits}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(classItem.status)}
                      </td>
                      <td className="px-6 py-4 text-gray-600 font-mono text-sm">
                        {classItem.crn}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Important Notes */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
            <FiAlertCircle size={20} />
            Important Registration Information
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>You must be registered for at least 12 credits to be considered a full-time student.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Changes to your schedule after the add/drop deadline may require approval.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Waitlisted classes are not guaranteed. Monitor your status regularly.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Contact your academic advisor if you have questions about your course selection.</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default MyRegistrationPage;
